import React, { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import PropTypes from "prop-types";
import { Col, Row } from "react-bootstrap";
import Dialog from "components/organisms/Dialog/Dialog";
import { useQueryClient } from "@tanstack/react-query";
import Button from "components/atoms/Button/Button";
import moment from "moment";
import errorToast from "helpers/errorToast";
import { FormattedMessage, useIntl } from "react-intl";
import { getLabelWithTranslationFallback } from "helpers/getLabel";
import { getRequestTimerSettings, updateRequestTimers } from "api";
import testIds from "constants/testIds";
import usePermissions from "hooks/usePermissions";
import "../ShipmentQuotes.scss";
import DatePicker from "components/atoms/DatePicker/DatePicker";
import TimeInput from "components/atoms/TimeInput/TimeInput";
import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";

const EditTimersDialog = function ({
  isOpen,
  close,
  pickupDate,
  requestTimers,
  requestId,
  fetchData,
  quoting_deadline_seconds_remaining,
}) {
  const intl = useIntl();
  const methods = useForm({
    defaultValues: {
      confirmation_date: requestTimers.confirmation_date,
      confirmation_time: requestTimers.confirmation_time,
      quoting_deadline_date: requestTimers.quoting_deadline_date,
      quoting_deadline_time: requestTimers.quoting_deadline_time,
    },
  });
  const queryClient = useQueryClient();
  const { permissions } = usePermissions();
  const { create_quote_requests, create_quick_requests } = JSON.parse(permissions).menu_items;

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReopenForQuotes, setIsReopenForQuotes] = useState(false);
  const [timingSettings, setTimingSettings] = useState({});

  // Retrieve timer settings
  useEffect(() => {
    if (!create_quote_requests && !create_quick_requests) return;
    setIsLoading(true);
    getTimerSettings();
  }, []);

  useEffect(() => {
    const { default_confirmation_time_days, use_default_confirmation_time } = timingSettings;
    if (!use_default_confirmation_time) return;
    // take the quoting deadline date time and add an amount of days to it equal to default_confirmation_time_days
    if (methods.getValues("quoting_deadline_date") && methods.getValues("quoting_deadline_time")) {
      const newConfirmationDatetime = moment(
        `${methods.getValues("quoting_deadline_date")} ${methods.getValues("quoting_deadline_time")}`,
        "YYYY-MM-DD HH:mm",
      ).add(default_confirmation_time_days, "days");
      methods.setValue(`confirmation_date`, newConfirmationDatetime.format("YYYY-MM-DD"));
      methods.setValue(`confirmation_time`, newConfirmationDatetime.format("HH:mm"));
    }
  }, [timingSettings, ...methods.watch(["quoting_deadline_date", "quoting_deadline_time"])]);

  const onClose = () => {
    close();
    setIsReopenForQuotes(false);
  };

  // Retrieve the request timer settings for the current user
  const getTimerSettings = () =>
    getRequestTimerSettings()
      .then((res) => {
        setTimingSettings(res.data);
        setIsLoading(false);
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "new-request.timing.couldNotRetrieveRequestTimer",
            defaultMessage: "Could not retrieve request timer settings. Please try again later.",
          }),
        ),
      );

  // Submits new request timers and closes the modal
  const handleEditTimers = (data) => {
    setIsSubmitting(true);

    updateRequestTimers(requestId, data)
      .then(() => {
        setTimeout(() => {
          fetchData();
          queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentBasicDetails(requestId) });
          setIsSubmitting(false);
          close();
        }, 1500);
      })
      .catch((error) => {
        setIsLoading(false);
        errorToast(error);
      });
  };

  const formatPickupDate = (date) => moment(date).format("YYYY-MM-DD");

  const isValidConfirmationDate = (value) =>
    methods.getValues("quoting_deadline_date") &&
    moment(
      `${methods.getValues("quoting_deadline_date")} ${methods.getValues("quoting_deadline_time")}`,
      "YYYY-MM-DD HH:mm",
    ).isBefore(moment(`${value} ${methods.getValues("confirmation_time")}`, "YYYY-MM-DD HH:mm"));

  const isQuotingDeadlinePassed = quoting_deadline_seconds_remaining < 0;
  const showQuotingDeadlineForm = (isQuotingDeadlinePassed && isReopenForQuotes) || !isQuotingDeadlinePassed;

  return (
    <Dialog
      size="sm"
      title={intl.formatMessage({
        id: "shipment-quotes.components.editTimersDialog.editRequestTimers",
        defaultMessage: "Edit request timers",
      })}
      show={isOpen}
      onHide={onClose}
      isLoading={isLoading}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleEditTimers)}>
          <div style={{ marginBottom: "32px" }}>
            <div className="d-flex">
              <div className="align-date d-flex flex-column mr-2" style={{ width: 190 }}>
                <label className={`${showQuotingDeadlineForm ? "required" : ""} no-wrap`}>
                  {getLabelWithTranslationFallback("Quoting deadline", {
                    translation: (
                      <FormattedMessage
                        id="shipment-quotes.components.editTimersDialog.quotingDeadline"
                        defaultMessage="Quoting Deadline"
                      />
                    ),
                  })}
                </label>
                {showQuotingDeadlineForm ? (
                  <DatePicker
                    name="quoting_deadline_date"
                    minDate={moment().toDate()}
                    required
                    registerOptions={{
                      deps: "confirmation_date",
                    }}
                  />
                ) : (
                  <>
                    <div className="mt-1 font-b4">
                      {moment(
                        `${methods.getValues("quoting_deadline_date")} ${methods.getValues("quoting_deadline_time")}`,
                      ).format("ddd, DD-MM-YYYY, h:mm a")}
                    </div>
                    <p onClick={() => setIsReopenForQuotes(true)} className="mt-2 font-b4 text-blue font-b4 cursor-pointer">
                      <FormattedMessage
                        id="shipment-quotes.components.editTimersDialog.reopenForQuotes"
                        defaultMessage="Reopen for quotes"
                      />
                    </p>
                  </>
                )}
              </div>
              {showQuotingDeadlineForm && (
                <div className="align-date d-flex flex-column">
                  <label className="required no-wrap">
                    <FormattedMessage id="general.time" defaultMessage="time" />
                  </label>
                  <TimeInput
                    name="quoting_deadline_time"
                    required
                    registerOptions={{ deps: "confirmation_date" }}
                    outputFormat="HH:mm"
                  />
                </div>
              )}
            </div>

            {moment(
              `${methods.getValues("quoting_deadline_date")} ${methods.getValues("quoting_deadline_time")}`,
              "YYYY-MM-DD HH:mm",
            ).isAfter(pickupDate) &&
              showQuotingDeadlineForm && (
                <div className="font-b5 mt-2" style={{ width: "250px", color: "#ea7939" }}>
                  <FormattedMessage
                    id="shipment-quotes.components.editTimersDialog.suggestedQuotingDeadline"
                    defaultMessage="The suggested quoting deadline exceeds the requested pickup date. ({date})"
                    values={{ date: formatPickupDate(pickupDate) }}
                  />
                </div>
              )}
          </div>

          <div>
            {!timingSettings.use_default_confirmation_time && (
              <div className="d-flex">
                <div className="align-date d-flex flex-column mr-2" style={{ width: 190 }}>
                  <label className="required no-wrap">
                    {getLabelWithTranslationFallback("Confirmation time", {
                      translation: (
                        <FormattedMessage
                          id="shipment-quotes.components.editTimersDialog.confirmationTime"
                          defaultMessage="Confirmation time"
                        />
                      ),
                    })}
                  </label>
                  <DatePicker
                    name="confirmation_date"
                    minDate={moment().toDate()}
                    required
                    validations={{
                      validDate: (v) =>
                        isValidConfirmationDate(v) || "The confirmation date must be greater than quoting deadline",
                    }}
                  />
                </div>
                <div className="align-date d-flex flex-column">
                  <label className="required no-wrap">
                    <FormattedMessage id="general.time" defaultMessage="time" />
                  </label>
                  <TimeInput
                    name="confirmation_time"
                    required
                    registerOptions={{ deps: "confirmation_date" }}
                    outputFormat="HH:mm"
                  />
                </div>
              </div>
            )}
            {timingSettings.use_default_confirmation_time && (
              <Col lg={6} md={6} className="mb-15 mb-lg-0">
                <div className="pb-2">
                  {getLabelWithTranslationFallback("Confirmation time", {
                    translation: (
                      <FormattedMessage
                        id="shipment-quotes.components.editTimersDialog.confirmationTime"
                        defaultMessage="Confirmation time"
                      />
                    ),
                  })}{" "}
                </div>
                <div className="font-b5">
                  {moment(
                    `${methods.getValues("confirmation_date")} ${methods.getValues("confirmation_time")}`,
                    "YYYY-MM-DD HH:mm",
                  ).format("DD MMMM YYYY HH:mm")}
                </div>
              </Col>
            )}
          </div>

          {!timingSettings.use_default_confirmation_time &&
            moment(
              `${methods.getValues("confirmation_date")} ${methods.getValues("confirmation_time")}`,
              "YYYY-MM-DD HH:mm",
            ).isAfter(moment(pickupDate)) && (
              <div className="font-b5 mt-2" style={{ width: "250px", color: "#ea7939" }}>
                <FormattedMessage
                  id="shipment-quotes.components.editTimersDialog.suggestedConfirmationTime"
                  defaultMessage="The suggested confirmation time exceeds the requested pickup date. ({date})"
                  values={{ date: formatPickupDate(pickupDate) }}
                />
              </div>
            )}

          <Row style={{ marginTop: "32px" }}>
            <Col lg={12} className="d-flex justify-content-between">
              <Button
                variant="outline-primary"
                label={intl.formatMessage({
                  id: "general.cancel",
                  defaultMessage: "Cancel",
                })}
                size="lg"
                onClick={onClose}
              />
              <Button
                isLoading={isSubmitting}
                variant="primary"
                type="submit"
                size="lg"
                label={intl.formatMessage({ id: "general.save", defaultMessage: "Save" })}
                data-testid={testIds.SHIPMENT_DASHBOARD_EDIT_TIMERS_CONFIRM_BUTTON}
              />
            </Col>
          </Row>
        </form>
      </FormProvider>
    </Dialog>
  );
};

EditTimersDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  pickupDate: PropTypes.string,
  requestTimers: PropTypes.instanceOf(Object).isRequired,
  requestId: PropTypes.string,
  fetchData: PropTypes.func.isRequired,
  quoting_deadline_seconds_remaining: PropTypes.number.isRequired,
};

EditTimersDialog.defaultProps = {
  pickupDate: "",
  requestId: "",
};

export default EditTimersDialog;
